import React from 'react';
import PropTypes from 'prop-types';
import { Bar, Line, Doughnut, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getDefaultChartOptions, getDataLabelsConfig } from '../utils/chartConfig';
import './ReportChart.css';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

/**
 * ReportChart Component
 * Wrapper for Chart.js with consistent styling
 */
const ReportChart = ({
    type = 'bar',
    data,
    options = {},
    title,
    height = 300,
    loading = false,
    id
}) => {
    // Merge default options with custom options
    const chartOptions = {
        ...getDefaultChartOptions(type),
        ...options,
        plugins: {
            ...getDefaultChartOptions(type).plugins,
            ...(options.plugins || {}),
            // Use refined datalabels config
            datalabels: {
                ...getDataLabelsConfig(options.dataType || 'value'),
                ...(options.plugins?.datalabels || {})
            }
        }
    };

    // Select chart component based on type
    const ChartComponent = {
        bar: Bar,
        line: Line,
        doughnut: Doughnut,
        pie: Pie
    }[type] || Bar;

    if (loading) {
        return (
            <div className="report-chart-container" style={{ height: `${height / 16}rem` }}>
                {title && <div className="report-chart-title">{title}</div>}
                <div className="report-chart-loading">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!data || !data.datasets || data.datasets.length === 0) {
        return (
            <div className="report-chart-container" style={{ height: `${height / 16}rem` }}>
                {title && <div className="report-chart-title">{title}</div>}
                <div className="report-chart-empty">
                    <i className="bi bi-bar-chart-line"></i>
                    <p>Aucune donnée disponible</p>
                </div>
            </div>
        );
    }

    return (
        <div className="report-chart-container" id={id}>
            {title && <div className="report-chart-title">{title}</div>}
            <div className="report-chart-wrapper" style={{ height: `${height / 16}rem` }}>
                <ChartComponent data={data} options={chartOptions} />
            </div>
        </div>
    );
};

ReportChart.propTypes = {
    type: PropTypes.oneOf(['bar', 'line', 'doughnut', 'pie']),
    data: PropTypes.object,
    options: PropTypes.object,
    title: PropTypes.string,
    height: PropTypes.number,
    loading: PropTypes.bool,
    id: PropTypes.string
};

export default ReportChart;
